import React, { memo, useCallback, useMemo } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  accordionSummaryClasses,
  Checkbox,
  FormControlLabel,
  formControlLabelClasses,
  FormGroup,
  ListItemText,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Grid from '@mui/material/Grid';
import { useEditEventCategory } from '../../../../redux/incident/EventCategory/hooks/useEditEventCategory';
import { getPolyglot } from '../../../../i18n';

interface PolicyEditCategoriesProps {
  selectedOptions: string[];
  setSelectedOptions: (...args: any[]) => void;
  availableEventIds: string[];
}

const eventIdLabelSx = {
  [`& .${formControlLabelClasses.label}`]: {
    wordBreak: 'break-word',
  },
} as const;

const accordionSummarySx = {
  px: '1em',
  [`& .${accordionSummaryClasses.content}`]: {
    maxWidth: 'calc(100% - 32px)',
  },
} as const;

const titleSx = {
  alignSelf: 'center',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
} as const;

const EventIdRow = memo(function EventIdRow({
  eventId,
  checked,
  onToggle,
}: {
  eventId: string;
  checked: boolean;
  onToggle: (eventId: string) => void;
}) {
  const handleChange = useCallback(
    () => onToggle(eventId),
    [onToggle, eventId]
  );
  return (
    <FormControlLabel
      control={
        <Checkbox checked={checked} onChange={handleChange} size="small" />
      }
      label={eventId}
      sx={eventIdLabelSx}
    />
  );
});

interface CategoryAccordionProps {
  categoryId: string;
  name: string;
  eventIds: string[];
  catState: 'some' | 'all' | 'none';
  selectedEventIdsInCategory: Set<string>;
  toggleEventId: (eventId: string) => string[];
  toggleCategory: (categoryId: string) => string[];
}

function areCategoryPropsEqual(
  prev: CategoryAccordionProps,
  next: CategoryAccordionProps
) {
  if (
    prev.categoryId !== next.categoryId ||
    prev.name !== next.name ||
    prev.catState !== next.catState ||
    prev.eventIds !== next.eventIds ||
    prev.toggleEventId !== next.toggleEventId ||
    prev.toggleCategory !== next.toggleCategory
  )
    return false;
  if (
    prev.selectedEventIdsInCategory.size !==
    next.selectedEventIdsInCategory.size
  )
    return false;
  for (const id of prev.selectedEventIdsInCategory) {
    if (!next.selectedEventIdsInCategory.has(id)) return false;
  }
  return true;
}

const CategoryAccordion = memo(function CategoryAccordion({
  categoryId,
  name,
  eventIds,
  catState,
  selectedEventIdsInCategory,
  toggleEventId,
  toggleCategory,
}: CategoryAccordionProps) {
  const handleCategoryChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.stopPropagation();
      toggleCategory(categoryId);
    },
    [toggleCategory, categoryId]
  );
  const handleCategoryClick = useCallback(
    (e: React.MouseEvent) => e.stopPropagation(),
    []
  );

  return (
    <Grid item xs={12} sm={6} md={4}>
      <Accordion slotProps={{ transition: { unmountOnExit: true } }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={accordionSummarySx}
        >
          <Checkbox
            checked={catState === 'all'}
            indeterminate={catState === 'some'}
            onChange={handleCategoryChange}
            onClick={handleCategoryClick}
            size="small"
          />
          <ListItemText
            primary={name}
            secondary={getPolyglot().t('incident.policy.edit.selected_alarms', {
              selected: selectedEventIdsInCategory.size,
              total: eventIds.length,
            })}
            primaryTypographyProps={{ fontWeight: 'bold', noWrap: true }}
            secondaryTypographyProps={{ noWrap: true }}
            sx={titleSx}
          />
        </AccordionSummary>
        <AccordionDetails>
          <FormGroup>
            {eventIds.map((eventId) => (
              <EventIdRow
                key={eventId}
                eventId={eventId}
                checked={selectedEventIdsInCategory.has(eventId)}
                onToggle={toggleEventId}
              />
            ))}
          </FormGroup>
        </AccordionDetails>
      </Accordion>
    </Grid>
  );
},
areCategoryPropsEqual);

function PolicyEditCategories({
  selectedOptions,
  setSelectedOptions,
  availableEventIds,
}: PolicyEditCategoriesProps) {
  const emit = useCallback(
    (newValues: string[]) => {
      setSelectedOptions({
        target: { name: 'filterValues', value: newValues },
      });
    },
    [setSelectedOptions]
  );
  const polyglot = getPolyglot();

  const {
    eventCategories,
    toggleEventId,
    toggleCategory,
    selectedCategoryDic,
  } = useEditEventCategory(availableEventIds, selectedOptions, emit);

  const selectedSet = useMemo(
    () => new Set(selectedOptions),
    [selectedOptions]
  );

  return (
    <Grid
      container
      spacing={1}
      alignItems="flex-start"
      sx={{ marginTop: '0px!important' }}
    >
      {eventCategories.map((category) => {
        const categorySelected = new Set(
          category.eventIds.filter((id) => selectedSet.has(id))
        );
        return (
          <CategoryAccordion
            key={category.id}
            categoryId={category.id}
            name={
              polyglot.t(`incident.policy.edit.categories.${category.id}`) ??
              category.name_en
            }
            eventIds={category.eventIds}
            catState={selectedCategoryDic[category.id]}
            selectedEventIdsInCategory={categorySelected}
            toggleEventId={toggleEventId}
            toggleCategory={toggleCategory}
          />
        );
      })}
    </Grid>
  );
}

export default PolicyEditCategories;
